/**
 * Turns every variant option group into a collapsible disclosure.
 *
 * This runs as progressive enhancement over the markup that variant-picker.liquid already produces, rather than
 * as changes to that (very large, product-specific) template: any group rendered as
 * `fieldset.variant-picker__option` with a `.variant-picker__option-info` header is picked up automatically,
 * including the hand-written ones for individual products. Groups without a header row are left alone, since
 * there would be nothing to click.
 *
 * Every group starts expanded, so nothing is hidden from a customer whose JavaScript never runs.
 */
(function () {
  const OPTION_SELECTOR = 'fieldset.variant-picker__option';
  const HEADER_SELECTOR = '.variant-picker__option-info';

  /* Clicks on these inside the header do their own thing (size chart, swatches, drawers). */
  const INTERACTIVE_SELECTOR = 'a, button, input, label, select, textarea, [role="button"], x-drawer, x-popover, dialog';

  const CHEVRON = '<svg role="presentation" focusable="false" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke-width="1.5"><path d="M2 4.5 6 8.5 10 4.5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path></svg>';

  let idCounter = 0;

  function setExpanded(option, expanded) {
    const panel = option.querySelector(':scope > .variant-picker__option-panel');
    const toggle = option.querySelector(':scope > .variant-picker__option-info > .variant-picker__option-toggle');

    option.classList.toggle('is-expanded', expanded);
    if (toggle) toggle.setAttribute('aria-expanded', String(expanded));
    if (!panel) return;

    if (expanded) {
      /* transitionend is the normal trigger; this covers browsers that do not animate grid-template-rows. */
      setTimeout(function () {
        if (option.classList.contains('is-expanded')) panel.classList.add('is-open-complete');
      }, 400);
    } else {
      panel.classList.remove('is-open-complete');
    }
  }

  function enhance(option) {
    if (option.dataset.collapsible === 'ready') return;

    const header = option.querySelector(':scope > ' + HEADER_SELECTOR);
    if (!header) return;

    option.dataset.collapsible = 'ready';

    const panel = document.createElement('div');
    panel.className = 'variant-picker__option-panel';
    panel.id = 'variant-option-panel-' + ++idCounter;

    const inner = document.createElement('div');
    inner.className = 'variant-picker__option-panel-inner';
    panel.appendChild(inner);

    /* Everything after the header becomes the panel body. Scripts stay put so they are not re-executed. */
    const body = [];
    let node = header.nextSibling;
    while (node) {
      const next = node.nextSibling;
      if (node.nodeName !== 'SCRIPT') body.push(node);
      node = next;
    }
    body.forEach(function (child) {
      inner.appendChild(child);
    });

    option.appendChild(panel);

    const legend = header.querySelector('legend');
    const name = legend ? legend.textContent.trim().replace(/[:：]\s*$/, '') : '';

    const toggle = document.createElement('button');
    toggle.type = 'button';
    toggle.className = 'variant-picker__option-toggle';
    toggle.setAttribute('aria-controls', panel.id);
    toggle.setAttribute('aria-expanded', 'true');
    if (name) toggle.setAttribute('aria-label', name);
    toggle.innerHTML = CHEVRON;
    header.appendChild(toggle);

    option.classList.add('variant-picker__option--collapsible', 'is-expanded');
    panel.classList.add('is-open-complete');

    toggle.addEventListener('click', function () {
      setExpanded(option, !option.classList.contains('is-expanded'));
    });

    /* The whole header row is clickable, except where something else already handles the click. */
    header.addEventListener('click', function (event) {
      if (event.target.closest(INTERACTIVE_SELECTOR)) return;
      setExpanded(option, !option.classList.contains('is-expanded'));
    });

    panel.addEventListener('transitionend', function (event) {
      if (event.propertyName !== 'grid-template-rows') return;
      panel.classList.toggle('is-open-complete', option.classList.contains('is-expanded'));
    });
  }

  function enhanceAll(root) {
    const scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(OPTION_SELECTOR).forEach(enhance);

    /* Mark the first group in each picker so it does not get a separator above it. */
    document.querySelectorAll('variant-picker').forEach(function (picker) {
      const first = picker.querySelector('.variant-picker__option--collapsible');
      if (first) first.classList.add('is-first');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      enhanceAll(document);
    });
  } else {
    enhanceAll(document);
  }

  /* Quick buy drawers and section re-renders inject pickers after load. */
  let scheduled = false;
  new MutationObserver(function (records) {
    if (scheduled) return;

    const addedElements = records.some(function (record) {
      return Array.prototype.some.call(record.addedNodes, function (node) {
        return node.nodeType === 1;
      });
    });
    if (!addedElements) return;

    scheduled = true;
    requestAnimationFrame(function () {
      scheduled = false;
      enhanceAll(document);
    });
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
